import React, { useMemo, useState } from "react";

import { useFetchPrintJobQuery } from "../../../api/printingApi";
import { useLanguage } from "../../../contexts/LanguageContext";
import { Btn, Drawer, Icon, Notice, Pill, Spinner, Thumb, cx } from "../../catalogProductStudio/edit/StudioUi";
import { describeVariant, fill, formatDateTime, plural } from "../printCenterModel";
import { STATUS_TONES } from "./PrintHistoryView";

/**
 * One print job: what was printed, why, by whom, each time printing was
 * invoked, and what has changed since. Reprints use current data and never
 * touch label demand.
 */
function PrintJobDrawer({ tt, jobId, onClose, onPrintAgain, onReprint, onDuplicate, onAwaitConfirmation }) {
  const { language } = useLanguage();
  const { data: job, isLoading } = useFetchPrintJobQuery(jobId, { skip: !jobId });
  const [selectedLineIds, setSelectedLineIds] = useState([]);

  const reprintableLines = useMemo(() => (job?.lines || []).filter((line) => line.current && !line.current.blockedReason), [job]);
  const changedCount = (job?.lines || []).filter((line) => line.changes.barcode || line.changes.price || line.changes.product).length;
  const canPrintAgain = job && (job.status === "generated" || job.status === "awaiting_confirmation");
  const chosen = selectedLineIds.length ? reprintableLines.filter((line) => selectedLineIds.includes(line.id)) : reprintableLines;

  return (
    <Drawer
      isOpen={Boolean(jobId)}
      onClose={onClose}
      size="lg"
      icon="history"
      closeLabel={tt("close")}
      title={job ? fill(tt("jobTitle"), { job: job.id }) : tt("loading")}
      description={
        job
          ? `${formatDateTime(job.createdAt, language)} · ${job.createdBy?.name || "—"} · ${tt(`jobSource_${job.source}`)}`
          : ""
      }
      footer={
        job ? (
          <div className="pc-drawer-actions">
            {job.status === "awaiting_confirmation" ? (
              <Btn variant="primary" icon="checkCircle" onClick={() => onAwaitConfirmation(job)}>
                {tt("confirmThisJob")}
              </Btn>
            ) : null}
            {canPrintAgain ? (
              <Btn icon="printer" onClick={() => onPrintAgain(job)}>{tt("printAgain")}</Btn>
            ) : null}
            <Btn icon="refresh" disabled={!chosen.length} onClick={() => onReprint(job, chosen)}>
              {selectedLineIds.length ? plural(tt, "reprintSelected", chosen.length) : tt("reprintAll")}
            </Btn>
            <Btn variant="ghost" icon="copy" disabled={!reprintableLines.length} onClick={() => onDuplicate(job, reprintableLines)}>
              {tt("duplicateSelection")}
            </Btn>
          </div>
        ) : null
      }
    >
      {isLoading || !job ? (
        <Spinner label={tt("loading")} />
      ) : (
        <div className="pc-job-detail">
          <div className="pc-job-detail__facts">
            <Pill tone={STATUS_TONES[job.status] || "neutral"}>{tt(`jobStatus_${job.status}`)}</Pill>
            <span>{plural(tt, "labelCount", job.totalLabels)}</span>
            {job.newStockLabels ? <span className="pc-demand-text">{fill(tt("traySplitNew"), { count: job.newStockLabels })}</span> : null}
            <span className="pc-muted">{job.preset?.name}</span>
            {job.reprintOf ? <span className="pc-muted">{fill(tt("reprintOf"), { job: job.reprintOf.id })}</span> : null}
            {job.resolvedAt ? (
              <span className="pc-muted">
                {fill(tt(job.status === "confirmed" ? "confirmedBy" : "discardedBy"), {
                  name: job.resolvedBy?.name || "—",
                  time: formatDateTime(job.resolvedAt, language),
                })}
              </span>
            ) : null}
          </div>
          <p className="pc-muted pc-small">{tt(`jobStatusHint_${job.status}`)}</p>

          {changedCount ? <Notice tone="warning">{plural(tt, "changedSince", changedCount)}</Notice> : null}

          <h3 className="pc-section-title">{tt("jobLines")}</h3>
          <ul className="pc-job-lines">
            {job.lines.map((line) => {
              const reprintable = reprintableLines.includes(line);
              return (
                <li key={line.id} className={cx("pc-job-line", !reprintable && "is-unavailable")}>
                  <input
                    type="checkbox"
                    className="pc-check"
                    disabled={!reprintable}
                    checked={selectedLineIds.includes(line.id)}
                    aria-label={fill(tt("selectItem"), { name: describeVariant(line.snapshot) })}
                    onChange={() =>
                      setSelectedLineIds((current) =>
                        current.includes(line.id) ? current.filter((id) => id !== line.id) : [...current, line.id]
                      )
                    }
                  />
                  <Thumb src={line.snapshot.imageUrl} size={36} fallbackLabel={line.snapshot.productTitle} />
                  <div className="pc-job-line__text">
                    <strong>{line.snapshot.productTitle}</strong>
                    <span className="pc-small">{describeVariant(line.snapshot)}</span>
                    <code className="pc-small">{line.snapshot.barcode}</code>
                    <span className="pc-job-line__flags">
                      {line.changes.unavailable ? <Pill tone="danger">{tt("changeUnavailable")}</Pill> : null}
                      {line.changes.barcode ? (
                        <Pill tone="danger" title={fill(tt("changeBarcodeTitle"), { from: line.snapshot.barcode, to: line.current?.barcode })}>
                          {tt("changeBarcode")}
                        </Pill>
                      ) : null}
                      {line.changes.price ? <Pill tone="warning">{tt("changePrice")}</Pill> : null}
                      {line.changes.product ? <Pill tone="warning">{tt("changeProduct")}</Pill> : null}
                    </span>
                  </div>
                  <div className="pc-job-line__qty">
                    <strong>{plural(tt, "labelCount", line.quantity)}</strong>
                    {line.newStockQuantity ? (
                      <span className="pc-small pc-demand-text">
                        {fill(tt("lineNewStockOf"), { newStock: line.newStockQuantity, total: line.quantity })}
                      </span>
                    ) : null}
                    {line.confirmedNewStock != null ? (
                      <span className="pc-small pc-muted">{fill(tt("lineConfirmed"), { count: line.confirmedNewStock })}</span>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>

          <h3 className="pc-section-title">{tt("attemptsTitle")}</h3>
          {!job.attempts.length ? (
            <p className="pc-muted pc-small">{tt("noAttempts")}</p>
          ) : (
            <ol className="pc-attempts">
              {job.attempts.map((attempt) => (
                <li key={attempt.id}>
                  <Icon name="printer" size={15} />
                  <span>
                    {fill(tt("attemptLine"), {
                      number: attempt.attemptNumber,
                      time: formatDateTime(attempt.createdAt, language),
                      name: attempt.createdBy?.name || "—",
                      start: attempt.startPosition,
                      slots: attempt.slotsPerPage,
                      labels: plural(tt, "labelCount", attempt.labelCount),
                      pages: plural(tt, "sheetCount", attempt.pageCount),
                    })}
                    {attempt.lineIds ? ` · ${tt("attemptPartial")}` : ""}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </Drawer>
  );
}

export default PrintJobDrawer;
